<template>
  <div class="log-viewer">
    <!-- Сводка ошибок -->
    <div v-if="errorSummary.length" class="error-summary">
      <div class="error-summary-title" @click="showErrorSummary = !showErrorSummary">
        <b-icon icon="alert-circle" size="is-small"></b-icon>
        <span>Найдено ошибок: {{ errorSummary.length }}</span>
        <b-icon
          :icon="showErrorSummary ? 'chevron-up' : 'chevron-down'"
          size="is-small"
        ></b-icon>
      </div>
      <div v-show="showErrorSummary" class="error-summary-list">
        <div
          v-for="(err, i) in errorSummary"
          :key="'err-' + i"
          class="error-summary-item"
          @click="scrollToBlock(err.blockIndex)"
        >
          <span class="error-index">#{{ i + 1 }}</span>
          <span class="error-text">{{ err.text }}</span>
        </div>
      </div>
    </div>

    <!-- Панель управления -->
    <div class="log-controls">
      <div class="log-controls-left">
        <b-dropdown aria-role="list" size="is-small">
          <template #trigger>
            <b-button size="is-small" icon-left="filter-variant">
              {{ levelLabel }}
            </b-button>
          </template>
          <b-dropdown-item @click="selectedLevel = 'DEBUG'">
            Всё (DEBUG)
          </b-dropdown-item>
          <b-dropdown-item @click="selectedLevel = 'INFO'">
            INFO и выше
          </b-dropdown-item>
          <b-dropdown-item @click="selectedLevel = 'WARNING'">
            WARNING и выше
          </b-dropdown-item>
          <b-dropdown-item @click="selectedLevel = 'ERROR'">
            Только ошибки
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div class="log-controls-right">
        <b-button size="is-small" icon-left="arrow-collapse-down" @click="scrollToFirstError">
          К ошибке
        </b-button>
        <b-button size="is-small" @click="toggleAllTracebacks">
          {{ allExpanded ? 'Свернуть стектрейсы' : 'Развернуть стектрейсы' }}
        </b-button>
        <b-button size="is-small" @click="toggleAllSteps">
          {{ allStepsExpanded ? 'Свернуть шаги' : 'Развернуть шаги' }}
        </b-button>
        <b-button
          size="is-small"
          icon-left="clock-outline"
          :type="relativeTime ? 'is-info' : ''"
          @click="relativeTime = !relativeTime"
        >
          {{ relativeTime ? 'Δt' : 'Время' }}
        </b-button>
        <b-button size="is-small" icon-left="content-copy" @click="copyLogs">
          Копировать
        </b-button>
        <b-button size="is-small" icon-left="download" @click="downloadLogs">
          Скачать
        </b-button>
      </div>
    </div>

    <!-- Логи -->
    <div class="log-lines" ref="logLines">
      <template v-for="(block, index) in filteredBlocks">

        <!-- Заголовок шага -->
        <div
          v-if="block.type === 'step'"
          :key="'step-' + index"
          class="log-step"
          :ref="'block-' + block.originalIndex"
        >
          <div
            class="log-step-header"
            :class="{
              'step-has-error': block.hasError,
              'step-skipped': block.stepStatus === 'SKIP',
              'step-ok': block.stepStatus === 'OK',
            }"
            @click="toggleStep(block.originalIndex)"
          >
            <b-icon
              :icon="expandedSteps[block.originalIndex] ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            ></b-icon>
            <span class="step-name">{{ block.stepName }}</span>
            <div class="step-meta">
              <b-tag
                v-if="block.stepDuration"
                size="is-small"
                type="is-dark"
              >
                {{ block.stepDuration }}
              </b-tag>
              <b-tag
                v-if="block.hasError"
                size="is-small"
                type="is-danger"
              >
                Ошибка
              </b-tag>
              <b-tag
                v-else-if="block.stepStatus === 'SKIP'"
                size="is-small"
                type="is-warning"
              >
                Пропущено
              </b-tag>
              <b-tag
                v-else-if="block.stepStatus === 'OK'"
                size="is-small"
                type="is-success"
              >
                OK
              </b-tag>
              <b-tag v-else size="is-small" type="is-light">
                {{ block.stepStatus || '...' }}
              </b-tag>
              <span class="step-line-count">
                {{ block.children.length }} строк
              </span>
            </div>
          </div>

                    <div
            v-show="expandedSteps[block.originalIndex]"
            class="log-step-body"
          >
            <template v-for="(child, ci) in block.children">
              <!-- Обычная строка -->
              <div
                v-if="child.type === 'line' && !child.isLong"
                :key="'sl-' + index + '-' + ci"
                class="log-line"
                :class="logLevelClass(child.level)"
                :ref="'block-' + child.originalIndex"
              >
                <span class="log-line-number">{{ child.lineNumber }}</span>
                <span class="log-timestamp">{{ displayTime(child.timestamp) }}</span>
                <span class="log-level" :class="'level-' + child.level">{{ child.level }}</span>
                <span class="log-message" v-html="linkify(child.message)"></span>
              </div>

              <!-- Длинная строка — сворачиваемая -->
              <div
                v-if="child.type === 'line' && child.isLong"
                :key="'sll-' + index + '-' + ci"
                class="log-long-line"
                :class="logLevelClass(child.level)"
                :ref="'block-' + child.originalIndex"
              >
                <div
                  class="log-long-header"
                  @click="toggleLongLine(child.originalIndex)"
                >
                  <b-icon
                    :icon="expandedLongLines[child.originalIndex] ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                  ></b-icon>
                  <span class="log-line-number">{{ child.lineNumber }}</span>
                  <span class="log-timestamp">{{ displayTime(child.timestamp) }}</span>
                  <span class="log-level" :class="'level-' + child.level">{{ child.level }}</span>
                  <span class="log-message-preview">{{ child.preview }}</span>
                  <b-tag size="is-small" type="is-dark" class="long-line-badge">
                    {{ child.message.length }} симв.
                  </b-tag>
                </div>
                <pre
                  v-show="expandedLongLines[child.originalIndex]"
                  class="log-long-body"
                  v-html="linkify(child.message)"
                ></pre>
              </div>

              <!-- Стектрейс -->
              <div
                v-if="child.type === 'traceback'"
                :key="'st-' + index + '-' + ci"
                class="log-traceback"
                :ref="'block-' + child.originalIndex"
              >
                <div class="traceback-header" @click="toggleTraceback(child.originalIndex)">
                  <b-icon
                    :icon="expandedTracebacks[child.originalIndex] ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                  ></b-icon>
                  <span class="traceback-summary">{{ child.errorLine }}</span>
                  <b-button
                    v-if="expandedTracebacks[child.originalIndex]"
                    size="is-small"
                    icon-left="content-copy"
                    class="traceback-copy"
                    @click.stop="copyText(child.text)"
                  ></b-button>
                </div>
                <pre
                  v-show="expandedTracebacks[child.originalIndex]"
                  class="traceback-body"
                  v-html="linkify(child.text)"
                ></pre>
              </div>

              <!-- Readable error -->
              <div
                v-if="child.type === 'readable-error'"
                :key="'sr-' + index + '-' + ci"
                class="log-readable-error"
                :ref="'block-' + child.originalIndex"
              >
                <b-icon icon="alert-circle-outline" size="is-small"></b-icon>
                <span v-html="linkify(child.message)"></span>
              </div>
            </template>
          </div>
        </div>

        <!-- Строка лога вне шага -->
        <div
          v-if="block.type === 'line' && !block.isLong"
          :key="'line-' + index"
          class="log-line"
          :class="logLevelClass(block.level)"
          :ref="'block-' + block.originalIndex"
        >
          <span class="log-line-number">{{ block.lineNumber }}</span>
          <span class="log-timestamp">{{ displayTime(block.timestamp) }}</span>
          <span class="log-level" :class="'level-' + block.level">{{ block.level }}</span>
          <span class="log-message" v-html="linkify(block.message)"></span>
        </div>

        <!-- Длинная строка лога — сворачиваемая -->
        <div
          v-if="block.type === 'line' && block.isLong"
          :key="'long-' + index"
          class="log-long-line"
          :class="logLevelClass(block.level)"
          :ref="'block-' + block.originalIndex"
        >
          <div
            class="log-long-header"
            @click="toggleLongLine(block.originalIndex)"
          >
            <b-icon
              :icon="expandedLongLines[block.originalIndex] ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            ></b-icon>
            <span class="log-line-number">{{ block.lineNumber }}</span>
            <span class="log-timestamp">{{ displayTime(block.timestamp) }}</span>
            <span class="log-level" :class="'level-' + block.level">{{ block.level }}</span>
            <span class="log-message-preview">{{ block.preview }}</span>
            <b-tag size="is-small" type="is-dark" class="long-line-badge">
              {{ block.message.length }} символов
            </b-tag>
          </div>
          <pre
            v-show="expandedLongLines[block.originalIndex]"
            class="log-long-body"
            v-html="linkify(block.message)"
          ></pre>
        </div>

        <!-- Стектрейс вне шага -->
        <div
          v-if="block.type === 'traceback'"
          :key="'tb-' + index"
          class="log-traceback"
          :ref="'block-' + block.originalIndex"
        >
          <div class="traceback-header" @click="toggleTraceback(block.originalIndex)">
            <b-icon
              :icon="expandedTracebacks[block.originalIndex] ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            ></b-icon>
            <span class="traceback-summary">{{ block.errorLine }}</span>
            <b-button
              v-if="expandedTracebacks[block.originalIndex]"
              size="is-small"
              icon-left="content-copy"
              class="traceback-copy"
              @click.stop="copyText(block.text)"
            ></b-button>
          </div>
          <pre
            v-show="expandedTracebacks[block.originalIndex]"
            class="traceback-body"
            v-html="linkify(block.text)"
          ></pre>
        </div>

        <!-- Readable error вне шага -->
        <div
          v-if="block.type === 'readable-error'"
          :key="'re-' + index"
          class="log-readable-error"
          :ref="'block-' + block.originalIndex"
        >
          <b-icon icon="alert-circle-outline" size="is-small"></b-icon>
          <span v-html="linkify(block.message)"></span>
        </div>

      </template>

      <div v-if="filteredBlocks.length === 0" class="log-empty">
        Логи пусты
      </div>
    </div>
  </div>
</template>

<script>
const LEVEL_PRIORITY = {
  DEBUG: 0,
  INFO: 1,
  WARNING: 2,
  WARN: 2,
  ERROR: 3,
  CRITICAL: 4,
  FATAL: 4,
};

// Регулярка для распознания строки лога с датой
const LOG_LINE_RE = /^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2},\d+)\s+-\s+(\w+)\s+(.*)/;

// Регулярка для ссылок
const URL_RE = /((https?:\/\/)[^\s'",)<>\]]+)/g;

export default {
  props: {
    logs: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      selectedLevel: 'INFO',
      relativeTime: false,
      showErrorSummary: true,
      expandedTracebacks: {},
      expandedSteps: {},
      expandedLongLines: {},
      allExpanded: false,
      allStepsExpanded: true,
    };
  },

  mounted() {
// Разворачиваем только шаги с ошибками, остальные сворачиваем
    this.parsedBlocks.forEach((block) => {
      if (block.type === 'step') {
        this.$set(this.expandedSteps, block.originalIndex, block.hasError);
      }
    });

    this.allStepsExpanded = false;

    // Автоскролл к первой ошибке
    this.$nextTick(() => {
      setTimeout(() => this.scrollToFirstError(), 300);
    });
  },

  computed: {
    levelLabel() {
      const labels = {
        DEBUG: 'Всё (DEBUG)',
        INFO: 'INFO и выше',
        WARNING: 'WARNING и выше',
        ERROR: 'Только ошибки',
      };
      return labels[this.selectedLevel] || 'Все';
    },

    firstTimestamp() {
      for (const block of this.parsedBlocks) {
        if (block.type === 'line' && block.timestamp) return block.timestamp;
        if (block.type === 'step' && block.children) {
          for (const child of block.children) {
            if (child.type === 'line' && child.timestamp) return child.timestamp;
          }
        }
      }
      return null;
    },

    parsedBlocks() {
      if (!this.logs) return [];

      const lines = this.logs.split('\n');
      const rawBlocks = this.parseLines(lines);
      return this.groupIntoSteps(rawBlocks);
    },

    filteredBlocks() {
      const minPriority = LEVEL_PRIORITY[this.selectedLevel] || 0;

      return this.parsedBlocks
        .map((block) => {
          if (block.type === 'step') {
            const filteredChildren = block.children.filter((child) =>
              this.isBlockVisible(child, minPriority)
            );
            if (filteredChildren.length === 0 && !block.hasError) return null;
            // Если у шага есть ошибка, показываем его даже если дети отфильтрованы
            if (filteredChildren.length === 0 && block.hasError) {
              return { ...block, children: block.children.filter(c => c.type === 'traceback' || c.type === 'readable-error' || (c.type === 'line' && c.level === 'ERROR')) };
            }
            return { ...block, children: filteredChildren };
          }
          return this.isBlockVisible(block, minPriority) ? block : null;
        })
        .filter(Boolean);
    },

    errorSummary() {
      const errors = [];
      const collect = (blocks) => {
        blocks.forEach((block) => {
          if (block.type === 'traceback') {
            errors.push({
              text: block.errorLine,
              blockIndex: block.originalIndex,
            });
          }
          if (block.type === 'step' && block.children) {
            collect(block.children);
          }
        });
      };
      collect(this.parsedBlocks);
      return errors;
    },
  },

  methods: {
    // ==================== Парсинг ====================
    toggleLongLine(index) {
      this.$set(this.expandedLongLines, index, !this.expandedLongLines[index]);
    },
    parseLines(lines) {
      const rawBlocks = [];
      let lineNumber = 0;
      let blockIndex = 0;
      let i = 0;

      while (i < lines.length) {
        const line = lines[i];
        lineNumber++;

        // --- Стектрейс ---
        if (line.match(/^Traceback \(most recent call last\):/)) {
          const result = this.collectTraceback(lines, i, lineNumber, blockIndex);
          rawBlocks.push(result.block);
          i = result.nextIndex;
          lineNumber += result.linesConsumed - 1;
          blockIndex++;
          continue;
        }

        // --- Error chain / Full traceback (продолжение ошибки) ---
        if (
          line.match(/^Error chain:/) ||
          line.match(/^Full traceback:/) ||
          line.match(/^\s+→\s+/)
        ) {
          const errorLines = [line];
          i++;
          lineNumber++;
          while (i < lines.length) {
            const nextLine = lines[i];
            if (nextLine.match(LOG_LINE_RE)) break;
            if (nextLine.match(/^Traceback/)) break;
            errorLines.push(nextLine);
            i++;
            lineNumber++;
          }
          rawBlocks.push({
            type: 'traceback',
            text: errorLines.join('\n'),
            errorLine: this.extractErrorLine(errorLines),
            lineNumber: lineNumber - errorLines.length,
            originalIndex: blockIndex++,
          });
          continue;
        }

        // --- Строка "The above exception..." без предшествующего Traceback ---
        if (line.match(/^The above exception/) || line.match(/^During handling/)) {
          const errorLines = [line];
          i++;
          lineNumber++;
          while (i < lines.length) {
            const nextLine = lines[i];
            if (nextLine.match(LOG_LINE_RE)) break;
            errorLines.push(nextLine);
            i++;
            lineNumber++;
          }
          rawBlocks.push({
            type: 'traceback',
            text: errorLines.join('\n'),
            errorLine: this.extractErrorLine(errorLines),
            lineNumber: lineNumber - errorLines.length,
            originalIndex: blockIndex++,
          });
          continue;
        }

        // --- Обычная строка лога ---
        const logMatch = line.match(LOG_LINE_RE);

        if (logMatch) {
          const [, timestamp, level, message] = logMatch;
          const isLong = message.length > 300;
          rawBlocks.push({
            type: 'line',
            timestamp,
            level,
            message,
            lineNumber,
            originalIndex: blockIndex++,
            isLong,
            preview: isLong ? message.substring(0, 100) + '...' : message,
          });
        } else if (line.trim()) {
          const isLong = line.length > 300;
          rawBlocks.push({
            type: 'line',
            timestamp: '',
            level: 'INFO',
            message: line,
            lineNumber,
            originalIndex: blockIndex++,
            isLong,
            preview: isLong ? line.substring(0, 100) + '...' : line,
          });
        }

        i++;
      }

      return rawBlocks;
    },

    collectTraceback(lines, startIndex, startLineNumber, blockIndex) {
      const tracebackLines = [lines[startIndex]];
      let i = startIndex + 1;
      let linesConsumed = 1;

      while (i < lines.length) {
        const nextLine = lines[i];

        // Новая строка лога с датой — стектрейс закончился
        if (nextLine.match(LOG_LINE_RE)) break;

        // Новый Traceback — это часть цепочки, но если мы уже собрали один,
        // проверяем есть ли "The above exception" перед ним
        if (
          nextLine.match(/^Traceback \(most recent call last\):/) &&
          tracebackLines.length > 1
        ) {
          // Проверяем, был ли перед этим "The above exception..."
          const prevLine = tracebackLines[tracebackLines.length - 1];
          if (
            prevLine.match(/^The above exception/) ||
            prevLine.match(/^During handling/)
          ) {
            // Это цепочка — продолжаем собирать
            tracebackLines.push(nextLine);
            i++;
            linesConsumed++;
            continue;
          }
          // Не цепочка — выходим
          break;
        }

        // "The above exception..." — часть цепочки
        if (
          nextLine.match(/^The above exception/) ||
          nextLine.match(/^During handling/)
        ) {
          tracebackLines.push(nextLine);
          i++;
          linesConsumed++;
          continue;
        }

        tracebackLines.push(nextLine);
        i++;
        linesConsumed++;
      }

      return {
        block: {
          type: 'traceback',
          text: tracebackLines.join('\n'),
          errorLine: this.extractErrorLine(tracebackLines),
          lineNumber: startLineNumber,
          originalIndex: blockIndex,
        },
        nextIndex: i,
        linesConsumed,
      };
    },

    extractErrorLine(lines) {
      for (let j = lines.length - 1; j >= 0; j--) {
        const line = lines[j].trim();
        if (!line) continue;
        if (line.startsWith('File ')) continue;
        if (line.startsWith('Traceback')) continue;
        if (line.startsWith('The above')) continue;
        if (line.startsWith('During handling')) continue;

        // Паттерн ошибки: "SomeError: message" или "Exception: message"
        if (line.match(/^\w+(\.\w+)*Error:|^\w+(\.\w+)*Exception:|^\w+Warning:/)) {
          return line.length > 200 ? line.substring(0, 200) + '...' : line;
        }

        return line.length > 200 ? line.substring(0, 200) + '...' : line;
      }
      return 'Traceback (нажмите чтобы развернуть)';
    },

    // ==================== Группировка по шагам ====================

    /**
     * Паттерны шагов:
     *   "* [Group] Download parametrization START"  — начало группы
     *   "* [Group] Download parametrization OK"     — конец группы
     *   "** Perform action: SomeAction"             — действие (может быть внутри группы или отдельно)
     *
     * Логика:
     *   - "* [Group] ... START" открывает шаг
     *   - "* [Group] ... OK/FAIL" закрывает шаг
     *   - "** Perform action: X" вне группы — отдельный шаг до следующего маркера
     */
    groupIntoSteps(rawBlocks) {
      const result = [];
      let currentStep = null;
      let i = 0;

      while (i < rawBlocks.length) {
        const block = rawBlocks[i];

        // Определяем тип маркера
        const groupStart = this.matchGroupStart(block);
        const groupEnd = this.matchGroupEnd(block);
        const actionStart = this.matchActionStart(block);

        // --- Конец группы ---
        if (groupEnd && currentStep && currentStep.isGroup) {
          currentStep.children.push(block);
          this.finalizeStep(currentStep);
          result.push(currentStep);
          currentStep = null;
          i++;
          continue;
        }

        // --- Начало группы ---
        if (groupStart) {
          // Закрываем предыдущий шаг если был
          if (currentStep) {
            this.finalizeStep(currentStep);
            result.push(currentStep);
          }

          currentStep = {
            type: 'step',
            isGroup: true,
            stepName: groupStart,
            stepStatus: null,
            stepDuration: null,
            hasError: false,
            timestamp: block.timestamp,
            lineNumber: block.lineNumber,
            originalIndex: block.originalIndex,
            children: [block],
          };
          i++;
          continue;
        }

        // --- Perform action вне группы ---
        if (actionStart && !currentStep) {
          // Закрываем предыдущий шаг если был
          if (currentStep) {
            this.finalizeStep(currentStep);
            result.push(currentStep);
          }

          currentStep = {
            type: 'step',
            isGroup: false,
            stepName: actionStart,
            stepStatus: null,
            stepDuration: null,
            hasError: false,
            timestamp: block.timestamp,
            lineNumber: block.lineNumber,
            originalIndex: block.originalIndex,
            children: [block],
          };
          i++;
          continue;
        }

        // --- Perform action внутри группы — вложенное действие ---
        if (actionStart && currentStep && currentStep.isGroup) {
          currentStep.children.push(block);
          i++;
          continue;
        }

        // --- Новый Perform action — закрывает предыдущий не-групповой шаг ---
        if (actionStart && currentStep && !currentStep.isGroup) {
          this.finalizeStep(currentStep);
          result.push(currentStep);

          currentStep = {
            type: 'step',
            isGroup: false,
            stepName: actionStart,
            stepStatus: null,
            stepDuration: null,
            hasError: false,
            timestamp: block.timestamp,
            lineNumber: block.lineNumber,
            originalIndex: block.originalIndex,
            children: [block],
          };
          i++;
          continue;
        }

        // --- Блок принадлежит текущему шагу ---
        if (currentStep) {
          currentStep.children.push(block);
          if (
            block.type === 'traceback' ||
            (block.type === 'line' && block.level === 'ERROR')
          ) {
            currentStep.hasError = true;
          }
          i++;
          continue;
        }

        // --- Блок вне любого шага ---
        result.push(block);
        i++;
      }

      // Закрываем последний шаг
      if (currentStep) {
        this.finalizeStep(currentStep);
        result.push(currentStep);
      }

      return result;
    },

    matchGroupStart(block) {
      if (block.type !== 'line') return null;
      const m = block.message.match(/^\*\s+\[Group\]\s+(.+?)\s+START/);
      return m ? m[1] : null;
    },

    matchGroupEnd(block) {
      if (block.type !== 'line') return false;
      return !!(
        block.message.match(/^\*\s+\[Group\]\s+.+?\s+(OK|FAIL|ERROR)/) ||
        block.message.match(/^\*\s+\[Group\]\s+.+?\s+SKIP/)
      );
    },

    matchActionStart(block) {
      if (block.type !== 'line') return null;
      const m = block.message.match(/^\*{2}\s+Perform action:\s+(.+)/);
      return m ? m[1] : null;
    },

    finalizeStep(step) {
      const children = step.children;

      // Определяем статус
      if (step.hasError) {
        step.stepStatus = 'ERROR';
      } else {
        const lastLine = [...children].reverse().find((c) => c.type === 'line');
        if (lastLine) {
          if (lastLine.message.match(/SKIP/i)) {
            step.stepStatus = 'SKIP';
          } else if (lastLine.message.match(/OK/)) {
            step.stepStatus = 'OK';
          } else {
            step.stepStatus = 'DONE';
          }
        } else {
          step.stepStatus = 'DONE';
        }
      }

      // Определяем длительность
      const timestamps = children
        .filter((c) => c.type === 'line' && c.timestamp)
        .map((c) => c.timestamp);

      if (timestamps.length >= 2) {
        const first = this.parseTimestamp(timestamps[0]);
        const last = this.parseTimestamp(timestamps[timestamps.length - 1]);
        const diff = last - first;
        if (!isNaN(diff) && diff >= 0) {
          if (diff < 1000) {
            step.stepDuration = `${diff}ms`;
          } else if (diff < 60000) {
            step.stepDuration = `${(diff / 1000).toFixed(1)}s`;
          } else {
            const min = Math.floor(diff / 60000);
            const sec = Math.floor((diff % 60000) / 1000);
            step.stepDuration = `${min}m ${sec}s`;
          }
        }
      }
    },

    // ==================== Время ====================

    parseTimestamp(ts) {
      if (!ts) return NaN;
      const cleaned = ts.replace(',', '.').replace(' ', 'T');
      return new Date(cleaned).getTime();
    },

    displayTime(timestamp) {
      if (!timestamp) return '';
      if (this.relativeTime) {
        return this.formatRelative(timestamp);
      }
      const match = timestamp.match(/(\d{2}:\d{2}:\d{2})/);
      return match ? match[1] : timestamp;
    },

    formatRelative(timestamp) {
      if (!this.firstTimestamp) return timestamp;
      const first = this.parseTimestamp(this.firstTimestamp);
      const current = this.parseTimestamp(timestamp);
      const diffMs = current - first;
      if (isNaN(diffMs)) return timestamp;
      const totalSec = Math.floor(diffMs / 1000);
      const ms = diffMs % 1000;
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      if (min > 0) {
        return `+${min}m${String(sec).padStart(2, '0')}.${String(ms).padStart(3, '0')}s`;
      }
      return `+${sec}.${String(ms).padStart(3, '0')}s`;
    },

    // ==================== Ссылки ====================

    linkify(text) {
      if (!text) return '';
      // Экранируем HTML, затем подсвечиваем ссылки
      const escaped = this.escapeHtml(text);
      return escaped.replace(
        URL_RE,
        '<a href="$1" target="_blank" class="log-link" onclick="event.stopPropagation()">$1</a>'
      );
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // ==================== Фильтрация ====================

    logLevelClass(level) {
      return {
        'log-debug': level === 'DEBUG',
        'log-info': level === 'INFO',
        'log-warning': level === 'WARNING' || level === 'WARN',
        'log-error': level === 'ERROR',
        'log-critical': level === 'CRITICAL' || level === 'FATAL',
      };
    },

    isBlockVisible(block, minPriority) {
      if (block.type === 'traceback' || block.type === 'readable-error') {
        return true;
      }
      const priority = LEVEL_PRIORITY[block.level] ?? 1;
      return priority >= minPriority;
    },

    // ==================== Действия ====================

    toggleTraceback(index) {
      this.$set(this.expandedTracebacks, index, !this.expandedTracebacks[index]);
    },

    toggleAllTracebacks() {
      this.allExpanded = !this.allExpanded;
      const setAll = (blocks) => {
        blocks.forEach((block) => {
          if (block.type === 'traceback') {
            this.$set(this.expandedTracebacks, block.originalIndex, this.allExpanded);
          }
          if (block.type === 'step' && block.children) {
            setAll(block.children);
          }
        });
      };
      setAll(this.parsedBlocks);
    },

    toggleStep(index) {
      this.$set(this.expandedSteps, index, !this.expandedSteps[index]);
    },

    toggleAllSteps() {
      this.allStepsExpanded = !this.allStepsExpanded;
      this.parsedBlocks.forEach((block) => {
        if (block.type === 'step') {
          this.$set(this.expandedSteps, block.originalIndex, this.allStepsExpanded);
        }
      });
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.log-traceback, .log-error, .step-has-error');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    scrollToBlock(blockIndex) {
      // Разворачиваем стектрейс
      this.$set(this.expandedTracebacks, blockIndex, true);

      // Разворачиваем родительский шаг
      this.parsedBlocks.forEach((block) => {
        if (block.type === 'step' && block.children) {
          const hasTarget = block.children.some((c) => c.originalIndex === blockIndex);
          if (hasTarget) {
            this.$set(this.expandedSteps, block.originalIndex, true);
          }
        }
      });

      this.$nextTick(() => {
        const ref = this.$refs['block-' + blockIndex];
        const el = ref && (Array.isArray(ref) ? ref[0] : ref);
        if (el) {
          const domEl = el.$el || el;
          domEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$buefy.toast.open({
          message: 'Скопировано в буфер обмена',
          type: 'is-success',
          duration: 2000,
        });
      });
    },

    copyLogs() {
      this.copyText(this.logs);
    },

    downloadLogs() {
      const blob = new Blob([this.logs], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `build-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.log-viewer {
  background: #1a1a2e;
  border-radius: 8px;
  padding: 0;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  overflow: hidden;
}

/* ========== Сводка ошибок ========== */
.error-summary {
  background: rgba(244, 67, 54, 0.12);
  border-bottom: 1px solid rgba(244, 67, 54, 0.3);
}

.error-summary-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  color: #ff8a80;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: rgba(244, 67, 54, 0.08);
  }
}

.error-summary-list {
  padding: 0 16px 10px;
}

.error-summary-item {
  display: flex;
  gap: 8px;
  padding: 4px 8px;
  color: #ef9a9a;
  cursor: pointer;
  border-radius: 4px;
  font-size: 12px;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
  }
}

.error-index {
  color: #ef5350;
  font-weight: bold;
  flex-shrink: 0;
}

/* ========== Панель управления ========== */
.log-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid #2a2a4a;
}

.log-controls-left,
.log-controls-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* ========== Логи ========== */
.log-lines {
  padding: 8px 0;
  max-height: 70vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a2e;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 4px;

    &:hover {
      background: #555;
    }
  }
}

/* ========== Строка лога ========== */
.log-line {
  padding: 1px 16px;
  display: flex;
  gap: 10px;
  line-height: 1.7;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
}

.log-line-number {
  color: #444;
  min-width: 32px;
  text-align: right;
  user-select: none;
  flex-shrink: 0;
  font-size: 11px;
}

.log-timestamp {
  color: #5a5a7a;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 12px;
}

.log-level {
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: bold;
  min-width: 55px;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.log-message {
  color: #ccc;
  word-break: break-word;
}

/* --- Уровни --- */
.log-debug {
  opacity: 0.5;
  .log-message { color: #666; }
  .level-DEBUG { color: #666; }
}

.log-info {
  .level-INFO { color: #4fc1ff; }
}

.log-warning {
  background: rgba(255, 167, 38, 0.06);
  .level-WARNING, .level-WARN { color: #ffa726; }
  .log-message { color: #ffd54f; }
}

.log-error {
  background: rgba(244, 67, 54, 0.08);
  .level-ERROR { color: #ef5350; }
  .log-message { color: #ef9a9a; }
}

.log-critical {
  background: rgba(244, 67, 54, 0.15);
  .level-CRITICAL, .level-FATAL {
    color: #ff1744;
    animation: pulse 2s infinite;
  }
  .log-message { color: #ff8a80; font-weight: bold; }
}

/* ========== Ссылки ========== */
::v-deep .log-link {
  color: #82b1ff;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;

  &:hover {
    color: #b3d4ff;
    text-decoration-style: solid;
  }
}

/* ========== Стектрейс ========== */
.log-traceback {
  margin: 4px 16px;
  border-left: 3px solid #ef5350;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(244, 67, 54, 0.05);
}

.traceback-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(244, 67, 54, 0.12);
  color: #ef9a9a;
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;

  &:hover {
    background: rgba(244, 67, 54, 0.2);
  }
}

.traceback-summary {
  color: #ff8a80;
  flex: 1;
  word-break: break-word;
}

.traceback-copy {
  opacity: 0.5;
  flex-shrink: 0;
  &:hover { opacity: 1; }
}

.traceback-body {
  background: rgba(0, 0, 0, 0.25);
  color: #888;
  padding: 12px 16px;
  margin: 0;
  font-size: 11px;
  line-height: 1.6;
  overflow-x: auto;
  border-top: 1px solid rgba(244, 67, 54, 0.15);
}

/* ========== Readable error ========== */
.log-readable-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  margin: 6px 16px;
  background: rgba(255, 167, 38, 0.1);
  border-left: 3px solid #ffa726;
  border-radius: 4px;
  color: #ffd54f;
  font-weight: 500;
  line-height: 1.6;
  font-size: 13px;
  white-space: pre-line;
}

/* ========== Шаги ========== */
.log-step {
  margin: 2px 0;
}

.log-step-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(79, 193, 255, 0.08);
  border-left: 3px solid #4fc1ff;
  cursor: pointer;
  color: #4fc1ff;
  font-weight: bold;
  font-size: 13px;

  &:hover {
    background: rgba(79, 193, 255, 0.14);
  }

  &.step-has-error {
    background: rgba(244, 67, 54, 0.08);
    border-left-color: #ef5350;
    color: #ef9a9a;

    &:hover {
      background: rgba(244, 67, 54, 0.14);
    }
  }

  &.step-skipped {
    background: rgba(255, 167, 38, 0.06);
    border-left-color: #ffa726;
    color: #ffd54f;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }

  &.step-ok {
    background: rgba(76, 175, 80, 0.06);
    border-left-color: #4caf50;
    color: #81c784;
  }
}

.step-name {
  flex: 1;
}

.step-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.step-line-count {
  color: #555;
  font-size: 11px;
  font-weight: normal;
}

.log-step-body {
  border-left: 1px solid #2a2a4a;
  margin-left: 16px;
}

/* ========== Пусто ========== */
.log-empty {
  padding: 40px 16px;
  text-align: center;
  color: #555;
  font-style: italic;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ========== Длинные строки ========== */
.log-long-line {
  margin: 2px 0;
}

.log-long-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 16px;
  cursor: pointer;
  line-height: 1.7;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.log-message-preview {
  color: #999;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.long-line-badge {
  flex-shrink: 0;
  opacity: 0.6;
}

.log-long-body {
  background: rgba(0, 0, 0, 0.2);
  color: #aaa;
  padding: 12px 16px;
  margin: 0 16px 4px;
  font-size: 11px;
  line-height: 1.5;
  overflow-x: auto;
  border-radius: 4px;
  border: 1px solid #2a2a4a;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>